import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../index';

interface UIState {
  theme: 'light' | 'dark';
  sidebarOpen: boolean;
  activeEditorTab: 'notes' | 'todos' | 'ai';
  showNewChapterModal: boolean;
  showVersionHistory: boolean;
  showExportModal: boolean;
  notifications: Notification[];
  loading: {
    global: boolean;
    memoir: boolean;
    chapter: boolean;
    auth: boolean;
  };
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  persistent?: boolean;
}

const initialState: UIState = {
  theme: (localStorage.getItem('theme') as 'light' | 'dark') || 'light',
  sidebarOpen: true,
  activeEditorTab: 'notes',
  showNewChapterModal: false,
  showVersionHistory: false,
  showExportModal: false,
  notifications: [],
  loading: {
    global: false,
    memoir: false,
    chapter: false,
    auth: false,
  },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
      localStorage.setItem('theme', action.payload);
    },
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
      localStorage.setItem('theme', state.theme);
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setActiveEditorTab: (state, action: PayloadAction<'notes' | 'todos' | 'ai'>) => {
      state.activeEditorTab = action.payload;
    },
    setShowNewChapterModal: (state, action: PayloadAction<boolean>) => {
      state.showNewChapterModal = action.payload;
    },
    setShowVersionHistory: (state, action: PayloadAction<boolean>) => {
      state.showVersionHistory = action.payload;
    },
    setShowExportModal: (state, action: PayloadAction<boolean>) => {
      state.showExportModal = action.payload;
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    setLoading: (state, action: PayloadAction<{ type: keyof UIState['loading']; loading: boolean }>) => {
      state.loading[action.payload.type] = action.payload.loading;
    },
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.global = action.payload;
    },
  },
});

export const {
  setTheme,
  toggleTheme,
  setSidebarOpen,
  toggleSidebar,
  setActiveEditorTab,
  setShowNewChapterModal,
  setShowVersionHistory,
  setShowExportModal,
  addNotification,
  removeNotification,
  clearNotifications,
  setLoading,
  setGlobalLoading,
} = uiSlice.actions;

export default uiSlice.reducer;

// Selectors
export const selectTheme = (state: RootState) => state.ui?.theme || 'light';
export const selectSidebarOpen = (state: RootState) => state.ui?.sidebarOpen || true;
export const selectActiveEditorTab = (state: RootState) => state.ui?.activeEditorTab || 'notes';
export const selectShowNewChapterModal = (state: RootState) => state.ui?.showNewChapterModal || false;
export const selectShowVersionHistory = (state: RootState) => state.ui?.showVersionHistory || false;
export const selectShowExportModal = (state: RootState) => state.ui?.showExportModal || false;
export const selectNotifications = (state: RootState) => state.ui?.notifications || [];
export const selectLoading = (state: RootState) => state.ui?.loading || { global: false, memoir: false, chapter: false, auth: false };
export const selectGlobalLoading = (state: RootState) => state.ui?.loading?.global || false;